import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { Draggable } from 'gsap/all';
import { useGSAP } from '@gsap/react';
import content from '../data/content.json';
import { resolveAssetPath, sortByOrderThenId } from '../utils/content';
import './PostersPage.css';

gsap.registerPlugin(Draggable);

const postersData = [...(content.posters ?? [])].sort(sortByOrderThenId);

const PostersPage = () => {
  const containerRef = useRef(null);
  const cardsRef = useRef([]);
  const [activeCard, setActiveCard] = useState(null);

  const scatterCards = (event) => {
    event?.stopPropagation();
    gsap.killTweensOf(cardsRef.current);

    const timeline = gsap.timeline();

    timeline.to(cardsRef.current, {
      x: 0,
      y: 0,
      rotation: 0,
      scale: 1,
      duration: 0.35,
      ease: 'power2.inOut',
      stagger: 0.04
    });

    timeline.to(cardsRef.current, {
      x: () => gsap.utils.random(-200, 200),
      y: () => gsap.utils.random(-120, 120),
      rotation: () => gsap.utils.random(-40, 40),
      scale: () => gsap.utils.random(0.9, 1.1),
      duration: 0.8,
      ease: 'power3.out',
      stagger: 0.06
    });

    setActiveCard(null);
  };

  const fanOutCards = (event) => {
    event?.stopPropagation();
    gsap.killTweensOf(cardsRef.current);

    const total = cardsRef.current.length;
    const angleStep = 15;
    const startAngle = -((total - 1) * angleStep) / 2;

    cardsRef.current.forEach((card, index) => {
      if (!card) return;

      const angle = startAngle + index * angleStep;
      gsap.to(card, {
        x: Math.sin((angle * Math.PI) / 180) * 200,
        y: Math.abs(Math.cos((angle * Math.PI) / 180) * 50) + 50,
        rotation: angle,
        scale: 1,
        zIndex: index,
        duration: 1,
        ease: 'power3.out',
        filter: 'blur(0px)'
      });
    });

    setActiveCard(null);
  };

  useGSAP(() => {
    if (postersData.length === 0) return;

    scatterCards();

    Draggable.create(cardsRef.current, {
      type: 'x,y',
      edgeResistance: 0.65,
      bounds: containerRef.current,
      onDragStart() {
        gsap.to(this.target, { scale: 1.05, duration: 0.2 });
        gsap.set(this.target, { zIndex: 100 });
      },
      onDragEnd() {
        gsap.to(this.target, { scale: 1, duration: 0.2 });
      }
    });
  }, { dependencies: [postersData.length], scope: containerRef });

  const handleCardClick = (index) => {
    if (activeCard === index) {
      scatterCards();
      return;
    }

    setActiveCard(index);

    cardsRef.current.forEach((card, cardIndex) => {
      if (!card) return;

      if (cardIndex === index) {
        gsap.to(card, {
          x: 0,
          y: -50,
          rotation: 0,
          scale: 1.2,
          zIndex: 100,
          duration: 0.8,
          ease: 'power3.out',
          filter: 'blur(0px)'
        });
      } else {
        gsap.to(card, {
          x: gsap.utils.random(-150, 150),
          y: gsap.utils.random(50, 100),
          rotation: gsap.utils.random(-25, 25),
          scale: 0.8,
          zIndex: 10,
          duration: 0.8,
          ease: 'power3.out',
          filter: 'blur(4px)'
        });
      }
    });
  };

  const handleMouseEnter = (index) => {
    if (activeCard !== null) return;
    const card = cardsRef.current[index];
    if (!card) return;

    gsap.to(card, {
      y: '-=20',
      scale: 1.05,
      duration: 0.3,
      ease: 'power2.out'
    });
  };

  const handleMouseLeave = (index) => {
    if (activeCard !== null) return;
    const card = cardsRef.current[index];
    if (!card) return;

    gsap.to(card, {
      y: '+=20',
      scale: 1,
      duration: 0.3,
      ease: 'power2.out'
    });
  };

  return (
    <div className="posters-page">
      <div className="posters-header">
        <Link to="/#Works" style={{ color: '#a855f7', textDecoration: 'none' }} />
      </div>

      <div className="posters-deck-container" ref={containerRef}>
        {(cardsRef.current = [])}

        {postersData.map((poster, index) => (
          <div
            key={poster.id ?? `${poster.title}-${index}`}
            className={`poster-card ${activeCard === index ? 'is-active' : ''}`}
            ref={(element) => {
              cardsRef.current[index] = element;
            }}
            onClick={() => handleCardClick(index)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={() => handleMouseLeave(index)}
          >
            <img src={resolveAssetPath(poster.image)} alt={poster.title || `Poster ${index + 1}`} className="poster-img" />
          </div>
        ))}

        <div className="posters-controls">
          <button className="poster-btn" onClick={scatterCards}>
          </button>
          <button className="poster-btn" onClick={fanOutCards}>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostersPage;
